'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Menu, X, ChevronDown, Settings, CreditCard, History, LogOut } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import type { User } from '@supabase/supabase-js'

export function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const accountRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      setUser(user)
      setLoading(false)
    }

    getUser()

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)
    })

    return () => subscription?.unsubscribe()
  }, [])

  // Close account dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false)
      }
    }
    if (accountOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [accountOpen])

  async function handleLogout() {
    const supabase = createClient()
    await supabase.auth.signOut()
    setAccountOpen(false)
    router.push('/')
  }

  // First letter of email for avatar
  const initials = user?.email?.[0]?.toUpperCase() ?? '?'

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-1 group">
            <span className="text-xl font-bold text-text-primary">
              the<span className="text-indigo">Normal</span>
            </span>
            <span className="text-xl font-bold text-indigo">.ai</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link
              href="/help"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Help
            </Link>
            <Link
              href="/news"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              News
            </Link>
            <Link
              href="/guides"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Guides
            </Link>
            {!user && (
              <Link
                href="/pricing"
                className="text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                Pricing
              </Link>
            )}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {!loading && !user && (
              <Link href="/signup">
                <Button className="hidden sm:inline-flex bg-indigo text-white hover:bg-indigo/90">
                  Get Help — Free
                </Button>
              </Link>
            )}

            {/* Account dropdown (authenticated) */}
            {!loading && user && (
              <div className="relative hidden md:block" ref={accountRef}>
                <button
                  onClick={() => setAccountOpen(prev => !prev)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm text-text-primary"
                  aria-label="Account menu"
                >
                  <span className="flex items-center justify-center w-10 h-10 rounded-full bg-indigo text-white text-sm font-semibold">
                    {initials}
                  </span>
                  <span className="max-w-[120px] truncate text-text-secondary text-xs">
                    {user.email}
                  </span>
                  <ChevronDown size={14} className="text-text-secondary flex-shrink-0" />
                </button>

                {accountOpen && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                    <Link
                      href="/account/settings"
                      className="flex items-center gap-2.5 px-4 py-3 text-sm text-text-primary hover:bg-gray-50 transition-colors"
                      onClick={() => setAccountOpen(false)}
                    >
                      <Settings size={15} className="text-text-secondary" />
                      Settings
                    </Link>
                    <Link
                      href="/account/billing"
                      className="flex items-center gap-2.5 px-4 py-3 text-sm text-text-primary hover:bg-gray-50 transition-colors"
                      onClick={() => setAccountOpen(false)}
                    >
                      <CreditCard size={15} className="text-text-secondary" />
                      Billing
                    </Link>
                    <Link
                      href="/account/sessions"
                      className="flex items-center gap-2.5 px-4 py-3 text-sm text-text-primary hover:bg-gray-50 transition-colors"
                      onClick={() => setAccountOpen(false)}
                    >
                      <History size={15} className="text-text-secondary" />
                      Sessions
                    </Link>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                    >
                      <LogOut size={15} />
                      Log out
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2.5 text-text-primary hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4 border-t border-gray-200">
            <nav className="flex flex-col gap-3 py-4">
              <Link
                href="/help"
                className="text-base text-text-secondary hover:text-text-primary transition-colors px-3 py-3"
                onClick={() => setIsOpen(false)}
              >
                Help
              </Link>
              <Link
                href="/news"
                className="text-base text-text-secondary hover:text-text-primary transition-colors px-3 py-3"
                onClick={() => setIsOpen(false)}
              >
                News
              </Link>
              <Link
                href="/guides"
                className="text-base text-text-secondary hover:text-text-primary transition-colors px-3 py-3"
                onClick={() => setIsOpen(false)}
              >
                Guides
              </Link>
              {!user && (
                <Link
                  href="/pricing"
                  className="text-base text-text-secondary hover:text-text-primary transition-colors px-3 py-3"
                  onClick={() => setIsOpen(false)}
                >
                  Pricing
                </Link>
              )}
              {!user && (
                <Link
                  href="/signup"
                  className="pt-2"
                  onClick={() => setIsOpen(false)}
                >
                  <Button className="w-full bg-indigo text-white hover:bg-indigo/90">
                    Get Help — Free
                  </Button>
                </Link>
              )}
              {user && (
                <>
                  <div className="border-t border-gray-100 pt-2 mt-1">
                    <p className="text-sm text-text-secondary px-2 pb-2 truncate">{user.email}</p>
                  </div>
                  <Link
                    href="/help"
                    className="flex items-center gap-2 text-base text-text-secondary hover:text-text-primary transition-colors px-3 py-3"
                    onClick={() => setIsOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    href="/account/settings"
                    className="flex items-center gap-2 text-base text-text-secondary hover:text-text-primary transition-colors px-3 py-3"
                    onClick={() => setIsOpen(false)}
                  >
                    Settings
                  </Link>
                  <Link
                    href="/account/billing"
                    className="flex items-center gap-2 text-base text-text-secondary hover:text-text-primary transition-colors px-3 py-3"
                    onClick={() => setIsOpen(false)}
                  >
                    Billing
                  </Link>
                  <Link
                    href="/account/sessions"
                    className="flex items-center gap-2 text-base text-text-secondary hover:text-text-primary transition-colors px-3 py-3"
                    onClick={() => setIsOpen(false)}
                  >
                    Sessions
                  </Link>
                  <button
                    onClick={() => { setIsOpen(false); handleLogout() }}
                    className="flex items-center gap-2 text-base text-red-600 hover:text-red-700 transition-colors px-3 py-3 w-full text-left"
                  >
                    Log out
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  )
}
